import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import compose from 'recompose/compose';
import { reduxForm, formValueSelector, Field, FieldArray, getFormValues, change } from 'redux-form';
import ApplicantData from './ApplicantData';
import FormImageUpload from '../legacyForm/FormImageUpload';
import FormSelect from '../legacyForm/FormSelect';
import FormCheckBoxWithLabel from '../legacyForm/FormCheckBoxWithLabel';
import AddIcon from '@material-ui/icons/AddBox';
import { Button, Divider, Typography } from '@material-ui/core';
import approvalConstraints from '../../constants/approvalConstraints';
import CategoryLocationsAutocompletion from './CategoryLocationsAutocompletion';
import BudgetApplicationFormItems from './BudgetApplicationFormItems';
import { ParticipantTypes } from '../../models/auth/ParticipantTypes';

export const validateForm = (form, props) => {
  const errors = {};

  if (!form.voter || !form.voter.participantType) errors.voter = { participantType: 'Wybór osoby zgłaszającej projekt jest wymagany' };

  if (!errors.voter && checkIfAccountTypeInvalid(props.account, form)) {
    errors.voter = { participantType: 'Wybór typu osoby zgłaszającej (osoba prywatna/jednostka) na formularzu jest niezgodny z kontem zalogowanego użytkownika' }
  }
  return errors;
};

const checkIfAccountTypeInvalid = (account, form) => {  
  if (account && account.data && account.data.participantType) {    
    const participantType = account.data.participantType;
    const voter = form.voter;
    if (voter && voter.participantType) {
      return (participantType == ParticipantTypes.legalEntity && voter.participantType != ParticipantTypes.legalEntity)
        || (participantType == ParticipantTypes.naturalPerson && voter.participantType != ParticipantTypes.naturalPerson);
    }
  }

  return false;
};

const selector = formValueSelector('applicationForm');

const required = value => value ? undefined : 'Pole jest wymagane';

let BudgetApplicationForm = ({
  handleSubmit,
  change,
  submitting,
  applicationForm,
  approvalConstraintOption,
  onApprovalConstraintPick,
  disabled,
  district,
  category,
  unit,
  costs,
  budget,
  parcelsAutocompletion,
  locations,
  voter,
  readonly,
  handleDraftSave,
  allValues,
  saving,
  gettingPdf,
  onGetPdfReport,
  showGetPdfButton,
  canSubmit,
  showSaveDraftButton,
  accountTypeInvalid }) => {
  const totalCost = costs ? costs.filter(c => c.value).map(c => c.value).reduce((a, b) => a + b, 0) : 0;
  const locationDependantCategory = category && applicationForm.categories.find(c => c.name === category).subcategoriesLocationDependent;
  const removeAttachment = () => {
    change('attachment', null);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="row">
        <div className="box-header-outside">
          <div className="col-sm-12">
            <div className="header">
              <h2>Formularz zgłoszenia</h2>
            </div>
          </div>
        </div>
        <BudgetApplicationFormItems
          applicationForm={applicationForm}
          change={change}
          budget={budget}
          parcelsAutocompletion={parcelsAutocompletion}
          locations={locations}
          totalCost={totalCost}
          readonly={readonly}
        />
        {applicationForm.districts && applicationForm.districts.length > 0 ? <div className="box">
          <div className="col-sm-12">
            <h3>Obszar</h3>
            <div>
              <Field
                name="district"
                component={FormSelect}
                label="Wybierz obszar"
                values={
                  applicationForm.districtsAttributes && applicationForm.districtsAttributes.length
                    ? applicationForm.districtsAttributes
                      .filter(c => !c.costDependent || (totalCost >= c.minimumCost && totalCost <= c.maximumCost))
                      .map(d => ({ value: d.name, name: d.name }))
                    : applicationForm.districts.map(d => ({ value: d, name: d }))}
                validate={required}
                onChange={() => {
                  change('category', null);
                }}
                disabled={readonly}
              />
            </div>
            {totalCost > 0 && applicationForm.districtsAttributes && applicationForm.districtsAttributes.length && !applicationForm.districtsAttributes
              .filter(c => !c.costDependent || (totalCost >= c.minimumCost && totalCost <= c.maximumCost))
              .length
              ? <Typography variant="caption" color="error" style={{ marginTop: 10 }}>Brak dostępnych obszarów dla wskazanego kosztu projektu</Typography> : null}
          </div>
        </div> : null}
        {applicationForm.categories && applicationForm.categories
          .filter(c => !applicationForm.districts || !applicationForm.districts.length || (c.districts && c.districts.some(d => d === district))).length > 0 ? <div className="box">
          <div className="col-sm-12">
            <h3>Kategoria</h3>
            <div>
              <Field
                name="category"
                component={FormSelect}
                label="Wybierz kategorię"
                values={applicationForm.categories
                  .filter(c => !c.costDependent || (totalCost >= c.minimumCost && totalCost <= c.maximumCost))
                  .filter(c => !applicationForm.districts || !applicationForm.districts.length || (c.districts && c.districts.some(d => d === district))).map(c => ({ value: c.name, name: c.name }))}
                validate={required}
                onChange={() => {
                  change('subcategory', null);
                }}
                disabled={readonly}
              />
            </div>
            {totalCost > 0 && !applicationForm.categories
              .filter(c => !c.costDependent || (totalCost >= c.minimumCost && totalCost <= c.maximumCost))
              .filter(c => !applicationForm.districts || !applicationForm.districts.length || (c.districts && c.districts.some(d => d === district))).map(c => ({ value: c.name, name: c.name }))
              .length
              ? <Typography variant="caption" color="error" style={{ marginTop: 10 }}>Brak dostępnych kategorii dla wskazanego kosztu projektu</Typography> : null}
          </div>
        </div> : null}
        {
          locationDependantCategory &&
          <div className="box">
            <div className="col-sm-12">
              <h3>Lokalizacja</h3>
              <Field
                name="subcategory"
                component={CategoryLocationsAutocompletion}
                subcategories={applicationForm.categories
                  .find(c => c.name === category).subcategories
                  .filter(c => !c.costDependent || (totalCost >= c.minimumCost && totalCost <= c.maximumCost))}
                onLocationSelected={subcategory => {
                  change('subcategory', subcategory);
                }}
                validate={required}
                disabled={readonly}
              />
            </div>
          </div>
        }
        {category && applicationForm.categories.find(c => c.name === category).subcategories && applicationForm.categories.find(c => c.name === category).subcategories.length > 0 ? <div className="box">
          <div className="col-sm-12">
            <h3>Podkategoria</h3>
            {locationDependantCategory &&
              <Typography variant="caption">Podkategoria zostanie wybrana na podstawie wskazanej powyżej lokalizacji</Typography>}
            <div>
              <Field
                name="subcategory"
                component={FormSelect}
                disabled={locationDependantCategory || readonly}
                label={locationDependantCategory ? '' : 'Wybierz podkategorię'}
                values={applicationForm.categories.find(c => c.name === category).subcategories
                  .filter(c => !c.costDependent || (totalCost >= c.minimumCost && totalCost <= c.maximumCost))
                  .map(c => ({ value: c.name, name: c.name }))}
                validate={required}
              />
            </div>
          </div>
        </div> : null}
        {applicationForm.listOfSupport && <div className="box">
          <div className="col-sm-12">
            <h3>Lista poparcia</h3>
            {applicationForm.listOfSupportDescription ? <p>{applicationForm.listOfSupportDescription}</p> : null}
            <div>
              <Field
                type="file"
                name="attachment"
                component={FormImageUpload}
                validate={required}
                readonly={readonly}
                onDelete={removeAttachment}
              />
            </div>
          </div>
        </div>}
        {applicationForm.eListOfSupportEnabled && <div className="box">
          <div className="col-sm-12">
            <h3>Elektroniczna lista poparcia</h3>
            {applicationForm.eListOfSupport.description ? <p>{applicationForm.eListOfSupport.description}</p> : null}
            <p>Adres elektronicznego poparcia zostanie wyświetlony po wysłaniu projektu</p>
          </div>
        </div>}
        {applicationForm.additionalAttachments && <div className="box">
          <div className="col-sm-12">
            <h3>Załączniki</h3>
            {applicationForm.additionalAttachmentsDescription ? <p>{applicationForm.additionalAttachmentsDescription}</p> : null}
            <div>
              <FieldArray name="attachments" component={({ fields }) => (
                <div>
                  {fields.map((name, index) => (
                    <div key={index} style={{ marginBottom: 10 }}>
                      <Field
                        type="file"
                        name={name}
                        component={FormImageUpload}
                        readonly={readonly}
                        onDelete={() => !readonly && fields.remove(index)}
                      />
                      <Divider />
                    </div>
                  ))}
                  <Button color="primary" onClick={() => fields.push()} disabled={readonly}>
                    <AddIcon style={{ marginRight: 8 }} /> Dodaj załącznik
                  </Button>
                </div>
              )} />
            </div>
          </div>
        </div>}
      </div>
      <div className="row">
        <div className="box-header-outside">
          <div className="col-sm-12">
            <div className="header">
              <h2>{applicationForm.voterRequiredDataDescription ? applicationForm.voterRequiredDataDescription : "Dane osoby zgłaszającej projekt"}</h2>
              <p>W przypadku kilku wnioskodawców proszę wpisać dane wnioskodawcy głównego</p>
            </div>
          </div>
        </div>
        <div className="box">
          <div className="col-sm-12">
            {voter && <ApplicantData voterRequiredData={applicationForm.voterRequiredData || {}} voter={voter} change={change} readonly={readonly} />}
            <div className="rodo" style={{ marginTop: 20, marginBottom: 20 }}>
              <div dangerouslySetInnerHTML={{ __html: unit.policy }} />
            </div>
            {canSubmit && applicationForm.predefinedApprovals.personalDataProcessing && <Field
              name="acceptPrivacyPolicy"
              component={FormCheckBoxWithLabel}
              label="Wyrażam zgodę na przetwarzanie moich danych osobowych dla potrzeb związanych z realizacją Budżetu Obywatelskiego"
              fullWidth
              validate={required}
              disabled={readonly}
            />}
            {canSubmit && applicationForm.predefinedApprovals.termsAndConditions && <Field
              name="acceptTerms"
              component={FormCheckBoxWithLabel}
              label={<div>Oświadczam, że zapoznałem/am się z treścią <a href="/regulamin" target="_blank" rel="noopener noreferrer">Regulaminu</a> oraz akceptuję jego postanowienia</div>}
              fullWidth
              validate={required}
              disabled={readonly}
            />}
            {canSubmit && applicationForm.approvals && applicationForm.approvals.length
              ? <React.Fragment>
                {applicationForm.approvals.filter(a => a.appliesTo === approvalConstraints.all.value).map(approval => (
                  <div key={approval.id}>
                    <Field
                      name={`approval_${approval.id}`}
                      component={FormCheckBoxWithLabel}
                      label={<div className="approval" dangerouslySetInnerHTML={{ __html: approval.value }} />}
                      fullWidth
                      validate={required}
                      disabled={readonly}
                    />
                  </div>
                ))}

                {!approvalConstraintOption && applicationForm.approvals.filter(a => a.appliesTo !== approvalConstraints.all.value).length ?
                  <React.Fragment>
                    <div className="row" style={{ justifyContent: 'center', opacity: readonly ? 0.65 : 1 }}>
                      <a href="#" onClick={e => { !readonly && onApprovalConstraintPick(approvalConstraints.minors.value); e.preventDefault(); }}
                        className="col-sm-6 col-md-4 obszar-link">
                        <div className="inner-wrapper">
                          <p>Wymagana zgoda rodzica/opiekuna prawnego</p>
                          <span href="#" className="btn">Wybierz</span>
                        </div>
                      </a>
                      <a href="#" onClick={e => { !readonly && onApprovalConstraintPick(approvalConstraints.adults.value); e.preventDefault(); }}
                        className="col-sm-6 col-md-4 obszar-link">
                        <div className="inner-wrapper">
                          <p>Niewymagana zgoda rodzica/opiekuna prawnego</p>
                          <span href="#" className="btn">Wybierz</span>
                        </div>
                      </a>
                    </div>
                    <Typography variant="caption" style={{ marginTop: 16 }}>Wymagane jest wybranie jednej opcji</Typography>
                  </React.Fragment>
                  : null}
                {approvalConstraintOption ?
                  applicationForm.approvals.filter(a => a.appliesTo === approvalConstraintOption).map(approval => (
                    <div key={approval.id}>
                      <Field
                        name={`approval_${approval.id}`}
                        component={FormCheckBoxWithLabel}
                        label={<div className="approval" dangerouslySetInnerHTML={{ __html: approval.value }} />}
                        fullWidth
                        validate={required}
                        disabled={readonly}
                      />
                    </div>
                  ))
                  : null}
              </React.Fragment>
              : null}
          </div>
        </div>
      </div>
      {showSaveDraftButton ?
        <div className="row" style={{ marginBottom: 5 }}>
          <div className="col-xs-12" style={{ marginLeft: 15 }}>
            Kliknij 'Zapisz wersję roboczą', aby wydrukować ostatnią wersję projektu
            </div></div>
        : null}
      <div className="row">
        <div className="col-xs-12 d-flex justify-content-start" style={{ marginLeft: 15 }}>
          {showGetPdfButton ?
            <button disabled={submitting || saving || gettingPdf} className="btn btn-send" variant="outlined"
              onClick={(e) => { onGetPdfReport(); e.preventDefault(); }} aria-label='Pobierz plik pdf' style={{ marginRight: 5 }}>
              {gettingPdf ? 'Drukowanie...' : 'Drukuj'} <i className="fas fa-print"></i>
            </button>
            : null}
          {showSaveDraftButton ?
            <button disabled={submitting || disabled || readonly || saving || gettingPdf} className="btn btn-send"
              onClick={e => { handleDraftSave(allValues); e.preventDefault(); }} aria-label='Zapisz wersję roboczą' variant="outlined">
              {saving ? 'Zapisywanie...' : 'Zapisz wersję roboczą'}
            </button>
            : null}
        </div>
      </div>
      {canSubmit ?
        <div className="row" style={{ marginBottom: 5 }}>
          <div className="col-xs-12 d-flex justify-content-end" style={{ marginRight: 15 }}>
            Po kliknięciu przycisku 'Zgłoś projekt' nie będzie możliwości powrotu do edycji projektu
          </div>
        </div>
        : null}
      <div className="row">
        <div className="col-xs-12" style={{ marginRight: 15 }}>
          <button type="submit" disabled={submitting || disabled || readonly || saving || gettingPdf || !canSubmit || accountTypeInvalid} className="btn btn-send" aria-label='Zgłoś projekt'>
            {submitting ? 'Zapisywanie...' : (budget.application && budget.application.changeRequests && budget.application.changeRequests.length ? 'Wyślij' : 'Zgłoś projekt')} <i className="fa fa-paper-plane" />
          </button>
        </div>
      </div>
    </form>
  );
};

BudgetApplicationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  details: PropTypes.array,
  applicationForm: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  district: PropTypes.string,
  category: PropTypes.string,
  unit: PropTypes.object.isRequired,
  costs: PropTypes.array,
  approvalConstraintOption: PropTypes.string,
  budget: PropTypes.object.isRequired,
  parcelsAutocompletion: PropTypes.bool,
  locations: PropTypes.array,
  onApprovalConstraintPick: PropTypes.func,
  voter: PropTypes.object,
  readonly: PropTypes.bool,
  allValues: PropTypes.object,
  saving: PropTypes.bool,
  onGetPdfReport: PropTypes.func,
  gettingPdf: PropTypes.bool,
  showGetPdfButton: PropTypes.bool,
  canSubmit: PropTypes.bool,
  showSaveDraftButton: PropTypes.bool,
  accountTypeInvalid: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    details: selector(state, 'details'),
    district: selector(state, 'district'),
    category: selector(state, 'category'),
    costs: selector(state, 'costs'),
    locations: selector(state, 'locations'),
    voter: selector(state, 'voter'),
    allValues: getFormValues('applicationForm')(state),
    saving: state.budget && state.budget.saving,
    account: state.budgetAccount,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ change }, dispatch);
}

BudgetApplicationForm = reduxForm({
  form: 'applicationForm',
  enableReinitialize: true,
  validate: validateForm,
})(BudgetApplicationForm);

export default compose(connect(mapStateToProps, mapDispatchToProps))(BudgetApplicationForm);



// WEBPACK FOOTER //
// ./src/components/budget/BudgetApplicationForm.js